import { useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FileText, Download, Database, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

function cycleOptions() {
  const opts = [];
  const now = new Date();
  for (let i = -6; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ value: key, label: `${d.toLocaleString("en-IN", { month: "short", year: "numeric" })} (24-23)` });
  }
  return opts;
}

async function downloadFile(url, filename) {
  const token = localStorage.getItem("token");
  const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export default function Reports() {
  const [cycle, setCycle] = useState(() => {
    const d = new Date();
    if (d.getDate() < 24) d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [restoring, setRestoring] = useState(false);

  const opts = cycleOptions();

  const dl = async (path, filename) => {
    try {
      await downloadFile(`${BACKEND_URL}/api/${path}`, filename);
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
  };

  const backup = async () => {
    try {
      const res = await api.get("/backup");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `mrs_hrms_backup_${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      toast.success("Backup downloaded");
    } catch { toast.error("Backup failed"); }
  };

  const restore = async (file) => {
    setRestoring(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post("/restore", data);
      toast.success("Restore complete — data restored");
    } catch (e) {
      toast.error("Restore failed: " + (e.response?.data?.detail || e.message));
    } finally { setRestoring(false); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Documents</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Reports</h1>
        <p className="text-slate-600 mt-1">Branded PDF and Excel exports — includes MRS Enterprise logo, signature, date and payroll period.</p>
      </div>

      <Card className="border-[#E5E2DC] p-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Payroll Cycle</Label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger className="mt-1 min-w-[260px]" data-testid="reports-cycle-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReportCard
          title="Payroll Report"
          description="Full cycle payroll summary with attendance breakdown, gross, deductions, advances and net payable per employee."
          onPdf={() => dl(`reports/payroll-pdf?cycle=${cycle}`, `payroll_${cycle}.pdf`)}
          onExcel={() => dl(`reports/payroll-excel?cycle=${cycle}`, `payroll_${cycle}.xlsx`)}
          testid="report-payroll"
        />
        <ReportCard
          title="Worker Information Report"
          description="Complete employee master with contact, KYC, bank details, wage and status."
          onPdf={() => dl("reports/employees-pdf", "employees.pdf")}
          onExcel={() => dl("reports/employees-excel", "employees.xlsx")}
          testid="report-employees"
        />
      </div>

      <Card className="border-[#E5E2DC] p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md bg-[#111111] text-white flex items-center justify-center">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-slate-900">Backup &amp; Restore</h3>
            <p className="text-sm text-slate-600">Download a JSON snapshot of the entire database, or restore from a previous backup.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button onClick={backup} variant="outline" data-testid="backup-button">
            <Download className="w-4 h-4 mr-1" /> Download Backup
          </Button>
          <label>
            <input
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && restore(e.target.files[0])}
              data-testid="restore-file-input"
            />
            <span className="inline-flex items-center px-4 py-2 rounded-md border border-[#E5E2DC] bg-white hover:bg-slate-50 cursor-pointer text-sm">
              <Upload className="w-4 h-4 mr-1" /> {restoring ? "Restoring..." : "Restore from Backup"}
            </span>
          </label>
        </div>
        <div className="text-xs text-slate-500 mt-3">
          Restore will merge/replace employees, attendance, transactions and settings from the uploaded JSON.
        </div>
      </Card>
    </div>
  );
}

const ReportCard = ({ title, description, onPdf, onExcel, testid }) => (
  <Card className="border-[#E5E2DC] p-6" data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-md bg-[#C59B27] text-white flex items-center justify-center">
        <FileText className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h3 className="font-serif text-xl text-slate-900">{title}</h3>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </div>
    </div>
    <div className="flex gap-2 mt-4">
      <Button variant="outline" onClick={onPdf} className="flex-1" data-testid={`${testid}-pdf`}>
        <FileText className="w-4 h-4 mr-1" /> Download PDF
      </Button>
      <Button variant="outline" onClick={onExcel} className="flex-1" data-testid={`${testid}-excel`}>
        <Download className="w-4 h-4 mr-1" /> Download Excel
      </Button>
    </div>
  </Card>
);
